import { NavLink, Outlet, useParams } from 'react-router-dom';
import { Shield } from 'lucide-react';

import { EmptyState } from '../../components/EmptyState';
import { Page, PageHeader } from '../../components/Page';
import { Skeleton } from '../../components/Skeleton';
import { useProject, useProjectPermissions } from '../../hooks/useProject';
import { cn } from '../../lib/cn';
import styles from './ProjectLayout.module.css';

const SETTINGS_TABS: readonly { readonly slug: string; readonly label: string }[] = [
  { slug: 'general', label: 'Основное' },
  { slug: 'boards', label: 'Доски' },
  { slug: 'components', label: 'Компоненты' },
  { slug: 'workflow', label: 'Workflow' },
  { slug: 'members', label: 'Участники' },
  { slug: 'agents', label: 'AI-агенты' },
  { slug: 'integrations', label: 'Интеграции' },
  { slug: 'wiki', label: 'Wiki' },
  { slug: 'danger', label: 'Опасная зона' },
];

/** Под-навигация настроек проекта внутри общего каркаса. */
export function ProjectLayout() {
  const { key } = useParams<{ key: string }>();
  const { project, isLoading, error } = useProject(key);
  const { canManageProject } = useProjectPermissions(project);

  if (isLoading) {
    return (
      <div className={styles['loading']}>
        <Skeleton variant="row" count={4} />
      </div>
    );
  }

  if (error || !project) {
    return (
      <EmptyState
        icon={<Shield size={20} />}
        title="Проект недоступен"
        description="Возможно, проект удалён или у вас нет к нему доступа."
      />
    );
  }

  if (!canManageProject) {
    return (
      <EmptyState
        icon={<Shield size={20} />}
        title="Нет доступа к настройкам"
        description="Настройки проекта доступны администратору проекта."
      />
    );
  }

  return (
    <Page width="normal">
      <PageHeader title={`Настройки · ${project.name}`} />
      <nav className={styles['tabs']} aria-label="Разделы настроек">
        {SETTINGS_TABS.map((tab) => (
          <NavLink
            key={tab.slug}
            to={`/projects/${key}/settings/${tab.slug}`}
            className={({ isActive }) => cn(styles['tab'], isActive && styles['tabActive'])}
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>
      <Outlet />
    </Page>
  );
}
